// src/components/payment/PaymentError.tsx
import React from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';

export const PaymentError: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get error reason from URL parameters
  const errorReason = searchParams.get('reason') || 'فشل في معالجة الدفع';
  const timestamp = searchParams.get('timestamp');

  const handleRetryPayment = () => {
    if (token) {
      navigate(`/pay/${token}`);
    }
  };

  const formatDateTime = (timestamp: string | null) => {
    if (!timestamp) return new Date().toLocaleString('ar-KW');
    
    const date = new Date(parseInt(timestamp) * 1000);
    return date.toLocaleString('ar-KW', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getErrorIcon = () => {
    return (
      <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
        <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-rose-50 py-8 px-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Error Header */}
          <div className="bg-gradient-to-r from-red-500 to-rose-600 text-white p-6 text-center">
            {getErrorIcon()}
            <h1 className="text-2xl font-bold">خطأ في الدفع</h1>
            <p className="text-red-100 mt-2">حدثت مشكلة أثناء معالجة دفعتك</p>
          </div>

          <div className="p-6">
            {/* Error Message */}
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-red-600 ml-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                <div>
                  <p className="font-medium text-red-800">خطأ:</p>
                  <p className="text-sm text-red-700 mt-1">{errorReason}</p>
                  {timestamp && (
                    <p className="text-xs text-red-600 mt-2">وقت الخطأ: {formatDateTime(timestamp)}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Common Reasons */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-amber-800 mb-2 flex items-center">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                الأسباب الشائعة لفشل الدفع:
              </h4>
              <ul className="text-sm text-amber-700 space-y-1">
                <li><strong>• إلغاء الدفع:</strong> ربما قمت بإلغاء عملية الدفع</li>
                <li><strong>• رصيد غير كافي:</strong> قد لا يوجد رصيد كافي في بطاقتك/حسابك</li>
                <li><strong>• رفض البطاقة:</strong> البنك قد رفض العملية</li>
                <li><strong>• مشكلة تقنية:</strong> مشكلة مؤقتة في الاتصال أو النظام</li>
                <li><strong>• انتهاء الجلسة:</strong> انتهت صلاحية جلسة الدفع</li>
              </ul>
            </div>

            {/* What to do next */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                ما يمكنك فعله:
              </h4>
              <ol className="text-sm text-blue-700 space-y-1">
                <li>1. تحقق من اتصالك بالإنترنت</li>
                <li>2. تأكد من صحة بيانات البطاقة والرصيد</li>
                <li>3. جرب عملية الدفع مرة أخرى</li>
                <li>4. اتصل بالبنك إذا استمرت المشكلة</li>
                <li>5. تواصل مع فريق الدعم للمساعدة</li>
              </ol>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              {token && (
                <button
                  onClick={handleRetryPayment}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-4 px-6 rounded-xl font-bold text-lg hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center"
                >
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  حاول الدفع مرة أخرى
                </button>
              )}
              
              <button
                onClick={() => window.close()}
                className="w-full bg-gray-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-gray-700 transition-colors flex items-center justify-center"
              >
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                إغلاق النافذة
              </button>
            </div>

            {/* Support Information */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-6">
              <h4 className="font-medium text-green-800 mb-2 flex items-center">
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
                تحتاج مساعدة؟
              </h4>
              <p className="text-sm text-green-700 mb-3">فريق الدعم متاح لمساعدتك:</p>
              <div className="space-y-2 text-sm">
                <div className="flex items-center">
                  <svg className="w-4 h-4 text-green-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <div>
                    <span className="font-medium text-green-800">دعم هاتفي: </span>
                    <span className="text-green-700">+965-56501847</span>
                    <br />
                    <span className="text-xs text-green-600">متاح 24/7</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Note */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 ml-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <div>
                  <p className="font-medium text-blue-800">ملاحظة أمان:</p>
                  <p className="text-sm text-blue-700 mt-1">
                    لم يتم معالجة أي دفعة. معلومات حسابك وبطاقتك تبقى آمنة.
                    {token && " رابط الدفع الخاص بك لا يزال صالحاً ويمكن استخدامه لإعادة المحاولة."}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};